import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchGlobalAnalysis, fetchStudentAnalysis } from "../../utils/analysisApi";
import Filter from "../../components/analysis-layout/filterbar";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Skeleton,
  Alert,
  Button,
  Avatar,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import LeaderboardRoundedIcon from "@mui/icons-material/LeaderboardRounded";
import AutoGraphRoundedIcon from "@mui/icons-material/AutoGraphRounded";



const MotionCard = motion(Card);
const MotionBox = motion(Box);

const glassMixin = {
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",
  background: "rgba(255,255,255,0.68)",
  border: "1px solid rgba(255,255,255,0.88)",
  boxShadow:
    "0 8px 32px rgba(99,102,241,0.09), 0 1px 0 rgba(255,255,255,0.9) inset",
};

const AmbientBackground = () => (
  <Box
    sx={{
      position: "fixed",
      inset: 0,
      zIndex: 0,
      pointerEvents: "none",
      overflow: "hidden",
      background:
        "linear-gradient(160deg, #f0f4ff 0%, #fafbff 50%, #f5f0ff 100%)",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: "-100px",
        right: "-60px",
        width: 420,
        height: 420,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)",
        filter: "blur(40px)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        bottom: "15%",
        left: "-60px",
        width: 340,
        height: 340,
        borderRadius: "50%",
        background:
          "radial-gradient(circle, rgba(16,185,129,0.08) 0%, transparent 70%)",
        filter: "blur(40px)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        backgroundImage:
          "radial-gradient(circle, rgba(99,102,241,0.06) 1px, transparent 1px)",
        backgroundSize: "28px 28px",
        maskImage:
          "radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)",
      }}
    />
  </Box>
);

const PERF_CONFIG = {
  Excellent: {
    color: "#10b981",
    bg: "rgba(16,185,129,0.1)",
    border: "rgba(16,185,129,0.25)",
    gradient: "linear-gradient(135deg, #10b981, #34d399)",
  },
  Good: {
    color: "#6366f1",
    bg: "rgba(99,102,241,0.1)",
    border: "rgba(99,102,241,0.25)",
    gradient: "linear-gradient(135deg, #6366f1, #818cf8)",
  },
  Average: {
    color: "#f59e0b",
    bg: "rgba(245,158,11,0.1)",
    border: "rgba(245,158,11,0.25)",
    gradient: "linear-gradient(135deg, #f59e0b, #fbbf24)",
  },
  Poor: {
    color: "#ef4444",
    bg: "rgba(239,68,68,0.08)",
    border: "rgba(239,68,68,0.2)",
    gradient: "linear-gradient(135deg, #ef4444, #f87171)",
  },
};

const TrendTooltip = ({ active, payload, label }) => {
  if (active && payload?.[0]) {
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)",
          border: "1px solid rgba(99,102,241,0.3)",
          borderRadius: "14px",
          px: 2,
          py: 1.5,
        }}
      >
        <Typography
          sx={{
            color: "#a5b4fc",
            fontSize: "0.68rem",
            fontWeight: 700,
            mb: 0.4,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontSize: "1.3rem",
            fontWeight: 800,
            fontFamily: "'Syne', sans-serif",
          }}
        >
          {payload[0].value?.toFixed(1)}%
        </Typography>
      </Box>
    );
  }
  return null;
};

const MasteryTooltip = ({ active, payload, label }) => {
  if (active && payload?.[0]) {
    return (
      <Box
        sx={{
          background: "linear-gradient(135deg, #022c22 0%, #064e3b 100%)",
          border: "1px solid rgba(16,185,129,0.3)",
          borderRadius: "14px",
          px: 2,
          py: 1.5,
        }}
      >
        <Typography
          sx={{
            color: "#6ee7b7",
            fontSize: "0.68rem",
            fontWeight: 700,
            mb: 0.4,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{
            color: "#fff",
            fontSize: "1.3rem",
            fontWeight: 800,
            fontFamily: "'Syne', sans-serif",
          }}
        >
          {payload[0].value?.toFixed(1)}%
        </Typography>
      </Box>
    );
  }
  return null;
};

const StatChip = ({ label, value, color, gradient, index }) => (
  <MotionBox
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.1 + index * 0.09 }}
    whileHover={{ y: -4, transition: { duration: 0.2 } }}
    sx={{
      p: 2,
      borderRadius: "18px",
      ...glassMixin,
      flex: "1 1 120px",
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: gradient,
      }}
    />
    <Typography
      sx={{
        fontFamily: "'Syne', sans-serif",
        fontSize: "1.75rem",
        fontWeight: 800,
        color: "#111827",
        lineHeight: 1.1,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.68rem",
        color: "#9ca3af",
        fontWeight: 700,
        mt: 0.4,
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </Typography>
  </MotionBox>
);

const SubjectList = ({ subjects, isStrong }) => {
  if (!subjects?.length)
    return (
      <Typography
        sx={{
          color: "#9ca3af",
          fontSize: "0.8rem",
          py: 1,
          textAlign: "center",
        }}
      >
        No data available
      </Typography>
    );
  return (
    <Stack spacing={0.5}>
      {subjects.map((s, i) => {
        const score = parseFloat(s.score || s.avg || 0).toFixed(1);
        const name = s.subject__name || s.name || "Unknown";
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: isStrong ? -12 : 12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.06 }}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                p: "10px 14px",
                borderRadius: "12px",
                background: isStrong
                  ? "rgba(16,185,129,0.06)"
                  : "rgba(239,68,68,0.05)",
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  flexShrink: 0,
                  background: isStrong ? "#10b981" : "#ef4444",
                }}
              />
              <Typography
                sx={{
                  flex: 1,
                  fontWeight: 600,
                  color: "#111827",
                  fontSize: "0.85rem",
                  fontFamily: "'Syne', sans-serif",
                }}
              >
                {name}
              </Typography>
              <Chip
                label={`${score}%`}
                size="small"
                sx={{
                  fontWeight: 800,
                  fontSize: "0.74rem",
                  height: 22,
                  background: isStrong
                    ? "rgba(16,185,129,0.1)"
                    : "rgba(239,68,68,0.08)",
                  color: isStrong ? "#10b981" : "#ef4444",
                  border: `1px solid ${
                    isStrong ? "rgba(16,185,129,0.25)" : "rgba(239,68,68,0.2)"
                  }`,
                  borderRadius: "7px",
                }}
              />
            </Stack>
          </motion.div>
        );
      })}
    </Stack>
  );
};

const PageSkeleton = () => (
  <Box sx={{ position: "relative", minHeight: "100vh" }}>
    <AmbientBackground />
    <Container maxWidth="lg" sx={{ py: 4, position: "relative", zIndex: 1 }}>
      <Skeleton width={160} height={36} sx={{ mb: 3 }} />
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        {[...Array(4)].map((_, i) => (
          <Skeleton
            key={i}
            variant="rounded"
            sx={{ flex: "1 1 120px", height: 96, borderRadius: "18px" }}
          />
        ))}
      </Box>
      <Skeleton
        variant="rounded"
        width="100%"
        height={260}
        sx={{ borderRadius: "24px", mb: 3 }}
      />
      <Skeleton
        variant="rounded"
        width="100%"
        height={310}
        sx={{ borderRadius: "24px" }}
      />
    </Container>
  </Box>
);

export default function RegularStudentAnalysisPage() {
  const { id } = useParams();

  // =========================
  // FILTER STATE
  // =========================
  const [filters, setFilters] = useState({
    term: "",
    session: "",
  });
  const [error, setError] = useState(null);
  
  const navigate = useNavigate();
  
  // =========================
  // DATA STATES
  // =========================
  const [data, setData] = useState(null);
  const [filterData, setFilterData] = useState({
    terms: [],
    sessions: [],
  });

  const [loading, setLoading] = useState(true);

  // =========================
  // 1. LOAD FILTER OPTIONS (FROM GLOBAL ANALYSIS)
  // =========================
  useEffect(() => {
    fetchGlobalAnalysis()
      .then((res) => {
        setFilterData({
          terms: res.terms || [],
          sessions: res.sessions || [],
        });
      })
      .catch((err) => {
        console.error("Filter load error:", err);
      
      });
  }, []);

  // =========================
  // 2. LOAD STUDENT ANALYSIS
  // =========================
  useEffect(() => {
    setLoading(true);

    fetchStudentAnalysis(id, filters.term, filters.session)
      .then((res) => {
        setData(res);
      })
      .catch((err) => {
        console.error("Analysis error:", err);
          setError(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, filters.term, filters.session]);

  // =========================
  // UI
  // =========================
   if (loading) return <PageSkeleton />;
   if (error)
     return (
       <Container sx={{ py: 4 }}>
         <Alert
           severity="error"
           sx={{ borderRadius: "20px" }}
           action={
             <Button onClick={() => navigate(-1)} size="small">
               Go Back
             </Button>
           }
         >
           Failed to load student analysis.
         </Alert>
       </Container>
     );
   if (!data) return <PageSkeleton />;

   const masteryData = (data.subject_mastery || []).map((s) => ({
     subject: s.subject__name || "Unknown",
     avg: parseFloat(s.avg || 0).toFixed(1) * 1,
   }));
   const progressData = (data.progress_trend || []).map((p) => ({
     date: p.date,
     avg_score: parseFloat(p.avg_score || 0),
   }));
   const perfConf = PERF_CONFIG[data.performance] || PERF_CONFIG["Average"];
   const initials = (data.student || "?")
     .split(" ")
     .slice(0, 2)
     .map((n) => n[0])
     .join("")
     .toUpperCase();

  return (
    <Box sx={{ position: "relative", minHeight: "100vh", paddingTop: "20px" }}>
      <AmbientBackground />

      <Container maxWidth="lg" sx={{ py: 4, position: "relative", zIndex: 1 }}>
        <Filter filters={filters} setFilters={setFilters} data={filterData} />
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate(-1)}
            sx={{
              mb: 3,
              color: "#4b5563",
              fontWeight: 700,
              px: 2,
              background: "rgba(255,255,255,0.8)",
              border: "1px solid rgba(0,0,0,0.1)",
              borderRadius: "12px",
              backdropFilter: "blur(12px)",
              "&:hover": { background: "rgba(255,255,255,0.95)" },
            }}
          >
            Back
          </Button>
        </motion.div>

        {/* student header */}
        <MotionBox
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                fontSize: "1.3rem",
                fontWeight: 800,
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                boxShadow: "0 8px 24px rgba(99,102,241,0.45)",
                fontFamily: "'Syne', sans-serif",
              }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 800,
                  color: "#111827",
                  letterSpacing: "-0.04em",
                }}
              >
                {data.student}
              </Typography>
              <Stack direction="row" spacing={1} mt={0.5}>
                <Chip
                  label={data.performance || "N/A"}
                  size="small"
                  sx={{
                    background: perfConf.bg,
                    color: perfConf.color,
                    fontWeight: 700,
                    border: `1px solid ${perfConf.border}`,
                    borderRadius: "8px",
                  }}
                />
                {data.position_in_class && (
                  <Chip
                    icon={<LeaderboardRoundedIcon />}
                    label={`Rank #${data.position_in_class}`}
                    size="small"
                    sx={{
                      background: "rgba(245,158,11,0.1)",
                      color: "#f59e0b",
                      fontWeight: 700,
                      border: "1px solid rgba(245,158,11,0.22)",
                      borderRadius: "8px",
                      "& .MuiChip-icon": { color: "inherit", fontSize: 14 },
                    }}
                  />
                )}
              </Stack>
            </Box>
          </Stack>
        </MotionBox>

        {/* stat chips */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
          <StatChip
            label="Average Score"
            value={`${parseFloat(data.average_score || 0).toFixed(1)}%`}
            color="#6366f1"
            gradient="linear-gradient(135deg, #6366f1, #818cf8)"
            index={0}
          />
          <StatChip
            label="Pass Rate"
            value={`${parseFloat(data.pass_rate || 0).toFixed(1)}%`}
            color="#10b981"
            gradient="linear-gradient(135deg, #10b981, #34d399)"
            index={1}
          />
          <StatChip
            label="Consistency Gap"
            value={
              data.consistency_gap != null
                ? parseFloat(data.consistency_gap).toFixed(1)
                : "—"
            }
            color="#8b5cf6"
            gradient="linear-gradient(135deg, #8b5cf6, #a78bfa)"
            index={2}
          />
          <StatChip
            label="Predicted Score"
            value={
              data.predicted_score != null
                ? `${parseFloat(data.predicted_score).toFixed(1)}%`
                : "—"
            }
            color="#f59e0b"
            gradient="linear-gradient(135deg, #f59e0b, #fbbf24)"
            index={3}
          />
        </Box>

        {/* recommendation */}
        {data.recommendation && (
          <MotionCard
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            sx={{
              mb: 3,
              borderRadius: "24px",
              overflow: "hidden",
              ...glassMixin,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: "linear-gradient(90deg, #f59e0b, #fbbf24)",
              }}
            />
            <CardContent sx={{ p: "20px !important" }}>
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "12px",
                    background: "rgba(245,158,11,0.12)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    border: "1px solid rgba(245,158,11,0.2)",
                  }}
                >
                  <LightbulbRoundedIcon
                    sx={{ color: "#f59e0b", fontSize: 20 }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.66rem",
                      color: "#f59e0b",
                      fontWeight: 700,
                      mb: 0.5,
                      letterSpacing: "0.1em",
                      fontFamily: "'Syne', sans-serif",
                    }}
                  >
                    RECOMMENDATION
                  </Typography>
                  <Typography
                    sx={{
                      color: "#111827",
                      fontWeight: 500,
                      lineHeight: 1.7,
                      fontSize: "0.9rem",
                    }}
                  >
                    {data.recommendation}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </MotionCard>
        )}

        {/* progress trend */}
        <MotionCard
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          sx={{
            mb: 3,
            borderRadius: "24px",
            overflow: "hidden",
            ...glassMixin,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: "linear-gradient(90deg, #6366f1, #8b5cf6)",
            }}
          />
          <CardContent sx={{ p: "24px !important" }}>
            <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
              <AutoGraphRoundedIcon sx={{ color: "#6366f1", fontSize: 22 }} />
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "'Syne', sans-serif",
                  fontWeight: 700,
                  color: "#111827",
                }}
              >
                Progress Trend
              </Typography>
            </Stack>
            {progressData.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.disabled">No trend data</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart
                  data={progressData}
                  margin={{ top: 8, right: 8, left: -16, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="studentGrad"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor="#6366f1"
                        stopOpacity={0.22}
                      />
                      <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,0,0,0.05)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11, fontWeight: 600, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11, fontWeight: 600, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip content={<TrendTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="avg_score"
                    stroke="#6366f1"
                    strokeWidth={2.5}
                    fill="url(#studentGrad)"
                    dot={{ fill: "#6366f1", r: 3.5, strokeWidth: 0 }}
                    activeDot={{
                      r: 7,
                      fill: "#6366f1",
                      stroke: "#fff",
                      strokeWidth: 2.5,
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </MotionCard>

        {/* subject mastery */}
        <MotionCard
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          sx={{
            mb: 3,
            borderRadius: "24px",
            overflow: "hidden",
            ...glassMixin,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              background: "linear-gradient(90deg, #10b981, #34d399)",
            }}
          />
          <CardContent sx={{ p: "24px !important" }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: "'Syne', sans-serif",
                fontWeight: 700,
                color: "#111827",
                mb: 0.5,
              }}
            >
              Subject Mastery
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#9ca3af", fontSize: "0.78rem", mb: 3 }}
            >
              Performance across all subjects
            </Typography>
            {masteryData.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography color="text.disabled">No mastery data</Typography>
              </Box>
            ) : (
              <ResponsiveContainer
                width="100%"
                height={Math.max(220, masteryData.length * 48)}
              >
                <BarChart
                  data={masteryData}
                  layout="vertical"
                  margin={{ top: 0, right: 64, left: 0, bottom: 0 }}
                  barSize={22}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(0,0,0,0.05)"
                    horizontal={false}
                  />
                  <XAxis
                    type="number"
                    domain={[0, 100]}
                    tick={{ fontSize: 11, fontWeight: 600, fill: "#9ca3af" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="subject"
                    width={100}
                    tick={{ fontSize: 12, fontWeight: 600, fill: "#4b5563" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<MasteryTooltip />}
                    cursor={{ fill: "rgba(16,185,129,0.05)", rx: 6 }}
                  />
                  <Bar dataKey="avg" radius={[0, 10, 10, 0]}>
                    {masteryData.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={
                          entry.avg >= 70
                            ? "#10b981"
                            : entry.avg >= 50
                            ? "#6366f1"
                            : "#ef4444"
                        }
                        fillOpacity={0.88}
                      />
                    ))}
                    <LabelList
                      dataKey="avg"
                      position="right"
                      formatter={(v) => `${v}%`}
                      style={{ fontSize: 11, fontWeight: 700, fill: "#4b5563" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </MotionCard>

        {/* strong / weak subjects */}
        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          <MotionCard
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            sx={{
              flex: "1 1 260px",
              borderRadius: "24px",
              overflow: "hidden",
              ...glassMixin,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: "linear-gradient(90deg, #10b981, #34d399)",
              }}
            />
            <CardContent sx={{ p: "24px !important" }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
                <StarRoundedIcon sx={{ color: "#10b981", fontSize: 22 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Syne', sans-serif",
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  Strong Subjects
                </Typography>
              </Stack>
              <SubjectList subjects={data.strong_subjects} isStrong={true} />
            </CardContent>
          </MotionCard>
          <MotionCard
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.38 }}
            sx={{
              flex: "1 1 260px",
              borderRadius: "24px",
              overflow: "hidden",
              ...glassMixin,
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 3,
                background: "linear-gradient(90deg, #ef4444, #f87171)",
              }}
            />
            <CardContent sx={{ p: "24px !important" }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
                <WarningAmberRoundedIcon
                  sx={{ color: "#ef4444", fontSize: 22 }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: "'Syne', sans-serif",
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  Weak Subjects
                </Typography>
              </Stack>
              <SubjectList subjects={data.weak_subjects} isStrong={false} />
            </CardContent>
          </MotionCard>
        </Box>
      </Container>
    </Box>
  );
}



 